import React, { useState, useEffect } from 'react';
import AIIntelligencePanel from './AIIntelligencePanel';
import {
  Megaphone, RefreshCw, AlertTriangle, Trophy, Target, Users,
} from 'lucide-react';
import {
  emptyMsg, cardWrap, neuShadow, btnSm,
} from './crmShared';

/* ═══════════════════════════════════════════════════════════════════════════
   MarketingDashboardTab — lead-source attribution insights.
   • Top sources by lead count
   • Conversion + win rate per source
   • $ won per source
   • Attribution coverage % (leads with vs without lead_source set)
   • 30/60/90-day trend sparkline
   ═══════════════════════════════════════════════════════════════════════════ */

const fmtMoney = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
};

export default function MarketingDashboardTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load(d = days) {
    setLoading(true);
    try {
      const r = await window.zignagenixAPI.getMarketingAnalytics({ days: d });
      setData(r);
    } catch (err) {
      console.error('Marketing analytics failed', err);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line
  useEffect(() => { load(days); }, [days]);

  return (
    <>
      {/* Phase F.2: AI Intelligence panel — silent role gate */}
      <AIIntelligencePanel kind="marketing" />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Megaphone size={18} color="#2E8B98" />
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#212529' }}>Marketing Dashboard</h2>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {[7, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{
              padding: '6px 12px', borderRadius: 999, border: 'none',
              background: days === d ? '#2E8B98' : '#F1F5F9',
              color: days === d ? '#fff' : '#475569',
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
            }}>{d}d</button>
          ))}
          <button onClick={() => load()} style={btnSm} title="Refresh">
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Top KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <KPICard icon={Users} color="#2E8B98" label="Total leads" value={data?.totalLeads ?? '—'} sub={`Last ${days} days`} />
        <KPICard icon={Target} color="#15803D" label="Attribution coverage" value={data ? `${data.attributionPct}%` : '—'}
          sub="Leads with a source set" />
        <KPICard icon={Trophy} color="#B45309" label="Top source"
          value={data?.bySource?.[0]?.source && data.bySource[0].source !== '(unattributed)'
            ? data.bySource[0].source
            : (data?.bySource?.[1]?.source || '—')}
          sub={data?.bySource?.[0]?.lead_count ? `${data.bySource[0].lead_count} leads` : null} />
      </div>

      {/* Attribution warning if coverage is low */}
      {data && data.attributionPct < 50 && data.totalLeads > 0 && (
        <div style={{
          ...cardWrap, padding: 12, marginBottom: 16,
          background: '#FEF3C7',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <AlertTriangle size={16} color="#B45309" />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#7C2D12' }}>
              Low attribution coverage
            </div>
            <div style={{ fontSize: 11, color: '#92400E', marginTop: 2 }}>
              Only {data.attributionPct}% of new leads have a lead_source set. Adding sources to the
              GHL contact import or training reps to fill it in will sharpen these metrics.
            </div>
          </div>
        </div>
      )}

      {/* Source breakdown */}
      <div style={{ ...cardWrap, padding: 14, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#212529', marginBottom: 10 }}>
          Lead Source Breakdown
        </div>
        {loading && !data ? (
          <div style={emptyMsg}>Loading…</div>
        ) : !data?.bySource?.length ? (
          <div style={emptyMsg}>No leads in the last {days} days.</div>
        ) : (
          <>
            <div style={{
              display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1.2fr',
              fontSize: 10, color: '#ADB5BD', fontWeight: 700, textTransform: 'uppercase',
              padding: '4px 8px',
            }}>
              <div>Source</div>
              <div>Leads</div>
              <div>Converted</div>
              <div>Conv %</div>
              <div>Won</div>
              <div>$ Won</div>
            </div>
            {data.bySource.map((r, i) => {
              const widthPct = data.bySource[0].lead_count > 0
                ? (r.lead_count / data.bySource[0].lead_count) * 100
                : 0;
              const isUnattributed = r.source === '(unattributed)';
              return (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1.2fr',
                  padding: '8px',
                  background: i % 2 === 0 ? '#fff' : '#FAFBFC',
                  borderRadius: 6,
                  alignItems: 'center',
                  fontSize: 12,
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                  {/* horizontal bar background */}
                  <div style={{
                    position: 'absolute', inset: 0,
                    width: `${widthPct}%`,
                    background: isUnattributed ? '#FEE2E2' : '#DBEAFE',
                    opacity: 0.45,
                    pointerEvents: 'none',
                  }} />
                  <div style={{
                    fontWeight: 600, position: 'relative',
                    color: isUnattributed ? '#991B1B' : '#212529',
                    fontStyle: isUnattributed ? 'italic' : 'normal',
                  }}>
                    {r.source}
                  </div>
                  <div style={{ position: 'relative', fontWeight: 700 }}>{r.lead_count}</div>
                  <div style={{ position: 'relative' }}>{r.converted_count}</div>
                  <div style={{ position: 'relative', color: r.conversion_pct >= 30 ? '#15803D' : r.conversion_pct >= 15 ? '#B45309' : '#475569' }}>
                    {r.conversion_pct}%
                  </div>
                  <div style={{ position: 'relative' }}>{r.won_count}</div>
                  <div style={{ position: 'relative', fontWeight: 700, color: r.won_amount > 0 ? '#15803D' : '#94A3B8' }}>
                    {fmtMoney(r.won_amount)}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Trend sparkline (text-only, simple) */}
      {data?.trend?.length > 0 && (
        <div style={{ ...cardWrap, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#212529', marginBottom: 10 }}>
            Daily Lead Trend
          </div>
          <Sparkline points={data.trend} />
        </div>
      )}

      <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 10, textAlign: 'center' }}>
        Conversion = leads moving past "New" stage. Win = won opportunity.
        Add a Lead Source to every contact (Add/Edit Contact modal) to improve coverage.
      </div>
    </>
  );
}

/* ─── KPI card ─────────────────────────────────────────────────────────────── */

function KPICard({ icon: Icon, color, label, value, sub }) {
  return (
    <div style={{ background: '#F8F9FA', borderRadius: 14, boxShadow: neuShadow, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Icon size={13} color={color} />
        <span style={{ fontSize: 10, color: '#6C757D', fontWeight: 700, textTransform: 'uppercase' }}>{label}</span>
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1.1, wordBreak: 'break-word' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#6C757D', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/* ─── Tiny SVG sparkline ───────────────────────────────────────────────────── */

function Sparkline({ points }) {
  if (!points || points.length === 0) return null;
  const w = 600, h = 60, pad = 4;
  const max = Math.max(...points.map(p => p.count), 1);
  const stepX = (w - pad * 2) / Math.max(1, points.length - 1);
  const path = points.map((p, i) => {
    const x = pad + i * stepX;
    const y = h - pad - (p.count / max) * (h - pad * 2);
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  return (
    <div style={{ overflow: 'auto' }}>
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <path d={path} fill="none" stroke="#2E8B98" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => {
          const x = pad + i * stepX;
          const y = h - pad - (p.count / max) * (h - pad * 2);
          return <circle key={i} cx={x} cy={y} r="2" fill="#2E8B98" />;
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#94A3B8', marginTop: 4 }}>
        <span>{points[0]?.day ? new Date(points[0].day).toLocaleDateString() : ''}</span>
        <span>peak: {max}/day</span>
        <span>{points[points.length - 1]?.day ? new Date(points[points.length - 1].day).toLocaleDateString() : ''}</span>
      </div>
    </div>
  );
}
